import { Injectable } from '@angular/core';
import {Skill, SkillLevel, UserSkillBind} from "../models/skill";
import {environment} from "@environments/environment";
import {RestApiManager} from "./rest-api-manager";
import {Paginator} from "../models/paginator";
import {Personnel} from "../models/personnel";
import {AccountManagerService} from "./account-manager-service";

@Injectable({
  providedIn: 'root'
})
export class SkillManagerService {

  constructor(
    private accountManagerService: AccountManagerService,
    private restApiManager: RestApiManager,
  ) { }

  async getAllSkills(paginator?: Paginator<Skill>): Promise<Skill[]> {
    return this.restApiManager.getAll<Skill>(
      Skill,
      `${environment.restApi.endpoint}/companies/${this.accountManagerService.account.company.id}/skills`,
      this.accountManagerService.account.authToken,
      { paginator }
    );
  }

  async getAllSkillLevels(paginator?: Paginator<SkillLevel>): Promise<SkillLevel[]> {
    return this.restApiManager.getAll<SkillLevel>(
      SkillLevel,
      `${environment.restApi.endpoint}/companies/${this.accountManagerService.account.company.id}/skill_levels`,
      this.accountManagerService.account.authToken,
      { paginator }
    );
  }

  async getSkillBinds(personnel: Personnel, paginator?: Paginator<UserSkillBind>): Promise<UserSkillBind[]> {
    return this.restApiManager.getAll<UserSkillBind>(
      UserSkillBind,
      `${environment.restApi.endpoint}/users/${personnel.id}/skills`,
      this.accountManagerService.account.authToken,
      { paginator }
    );
  }

  public createSkill(skill: Skill) {
    return this.restApiManager.post<Skill>(
      `${environment.restApi.endpoint}/skills`,
      { ...skill, companyId: this.accountManagerService.account.company.id },
      this.accountManagerService.account.authToken
    );
  }

  public async updateSkill(skillId: string, skill: Skill): Promise<Skill> {
    return this.restApiManager.put<Skill>(
      `${environment.restApi.endpoint}/skills/${skillId}`,
      { ...skill, companyId: this.accountManagerService.account.company.id },
      this.accountManagerService.account.authToken
    );
  }

  public async removeSkill(skillId: string): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/skills/${skillId}`,
      this.accountManagerService.account.authToken
    );
  }

  public createSkillLevel(skillLevel: SkillLevel) {
    return this.restApiManager.post<SkillLevel>(
      `${environment.restApi.endpoint}/skill_levels`,
      { ...skillLevel, companyId: this.accountManagerService.account.company.id },
      this.accountManagerService.account.authToken
    );
  }

  public async updateSkillLevel(skillLevelId: string, skillLevel: SkillLevel): Promise<SkillLevel> {
    return this.restApiManager.put<SkillLevel>(
      `${environment.restApi.endpoint}/skill_levels/${skillLevelId}`,
      { ...skillLevel, companyId: this.accountManagerService.account.company.id },
      this.accountManagerService.account.authToken
    );
  }

  public async removeSkillLevel(skillLevelId: string): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/skill_levels/${skillLevelId}`,
      this.accountManagerService.account.authToken
    );
  }

  public bindSkill(personnel: Personnel, skill: Skill, skillLevel: SkillLevel): Promise<UserSkillBind> {
    return this.restApiManager.post<UserSkillBind>(
      `${environment.restApi.endpoint}/users/${personnel.id}/skills`,
      { skillId: skill.id, skillLevelId: skillLevel.id, startAt: Date.now(), expiringAt: Date.now() + skill.validityInDays * 24 * 3600 * 1000 },
      this.accountManagerService.account.authToken
    );
  }

  public updateSkillBinding(personnel: Personnel, skill: Skill, skillLevel: SkillLevel): Promise<UserSkillBind> {
    return this.restApiManager.put<UserSkillBind>(
      `${environment.restApi.endpoint}/users/${personnel.id}/skills/${skill.id}`,
      { skillId: skill.id, skillLevelId: skillLevel.id, startAt: Date.now(), expiringAt: Date.now() + skill.validityInDays * 24 * 3600 * 1000 },
      this.accountManagerService.account.authToken
    );
  }

  public async removeSkillBinding(personnel: Personnel, skillId: string): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/users/${personnel.id}/skills/${skillId}`,
      this.accountManagerService.account.authToken
    );
  }

}
